import React from 'react';
import { shouldForceLocalMode } from './StorageDriver.js';

/**
 * Shared icon library hook. Loads the iconLibrary table once on first use
 * and caches the result module-wide so every consumer (ItemIcon,
 * RpgIconPicker, IconSelectButton, …) shares a single fetch.
 *
 * Returns:
 *   ready      — true once the fetch resolves (or fails irrecoverably)
 *   byKey      — { [key]: { key, name, categories[], subcategory, tags[], url } }
 *   allIcons   — sorted array of the same records (for the picker grid)
 *   categories — unique top-level category names (filter chips)
 *   tagCounts  — { [tag]: count } for tag-filter UI
 *
 * If REACT_APP_CONVEX_URL is unset (static demo distribution), the hook
 * resolves to ready=true with an empty library; consumers fall back to
 * emoji icons via the existing fallback path in ItemIcon.
 */

let _cache = null;          // resolved value, shared across components
let _inflight = null;       // promise during first load
const _subscribers = new Set();

const emit = (value) => {
  _cache = value;
  for (const sub of _subscribers) {
    try { sub(value); } catch (e) { /* swallow */ }
  }
};

const buildIndices = (rows) => {
  const byKey = {};
  const tagCounts = {};
  const cats = new Set();
  for (const r of rows) {
    byKey[r.key] = r;
    for (const c of r.categories) cats.add(c);
    for (const t of r.tags) tagCounts[t] = (tagCounts[t] || 0) + 1;
  }
  return {
    ready: true,
    byKey,
    allIcons: rows.slice().sort((a, b) => a.name.localeCompare(b.name)),
    categories: Array.from(cats).sort(),
    tagCounts,
  };
};

const loadIconLibrary = async () => {
  const url = process.env.REACT_APP_CONVEX_URL;
  if (!url || shouldForceLocalMode()) {
    return buildIndices([]);
  }
  const [{ ConvexHttpClient }, { api }] = await Promise.all([
    import('convex/browser'),
    import('../convex/_generated/api'),
  ]);
  const client = new ConvexHttpClient(url);
  const rows = await client.query(api.icons.list, {});
  return buildIndices(rows);
};

const ensureLoaded = () => {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = loadIconLibrary()
    .then((value) => { emit(value); _inflight = null; return value; })
    .catch((err) => {
      console.error('Failed to load icon library:', err);
      const empty = buildIndices([]);
      emit(empty);
      _inflight = null;
      return empty;
    });
  return _inflight;
};

const EMPTY = buildIndices([]);
const PENDING = { ...EMPTY, ready: false };

// Non-reactive cache reader for callsites that just need a quick "does this
// key resolve to a real icon?" check inside a conditional. Returns null
// until the library finishes loading. Components that need to refresh when
// the library arrives should use the hook instead.
export function getIconByKey(key) {
  if (!_cache || !key) return null;
  return _cache.byKey[key] || null;
}

export function useIconLibrary() {
  const [, forceUpdate] = React.useReducer((n) => n + 1, 0);
  React.useEffect(() => {
    const sub = () => forceUpdate();
    _subscribers.add(sub);
    if (!_cache) ensureLoaded();
    return () => { _subscribers.delete(sub); };
  }, []);
  return _cache || PENDING;
}
